import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

// ─── Leads ───────────────────────────────────────────────
export const fetchLeads = createAsyncThunk(
  'crm/fetchLeads',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/crm/leads', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch leads');
    }
  }
);

export const createLead = createAsyncThunk(
  'crm/createLead',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/crm/leads', data);
      return response.data.lead || response.data;
    } catch (error) {
      // Surface the full 409 duplicate payload ({ duplicate, existingLead }) so the UI
      // can offer "view existing" vs "force create" instead of just showing an error toast.
      if (error.response?.status === 409 && error.response?.data?.duplicate) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Failed to create lead');
    }
  }
);

export const updateLead = createAsyncThunk(
  'crm/updateLead',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/crm/leads/${id}`, data);
      return response.data.lead || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Failed to update lead');
    }
  }
);

export const deleteLead = createAsyncThunk(
  'crm/deleteLead',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/crm/leads/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete lead');
    }
  }
);

export const convertLead = createAsyncThunk(
  'crm/convertLead',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/crm/leads/${id}/convert`, data);
      return response.data.lead || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Failed to convert lead');
    }
  }
);

// ─── Deals ───────────────────────────────────────────────
export const fetchDeals = createAsyncThunk(
  'crm/fetchDeals',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/crm/deals', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch deals');
    }
  }
);

export const createDeal = createAsyncThunk(
  'crm/createDeal',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/crm/deals', data);
      return response.data.deal || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Failed to create deal');
    }
  }
);

export const updateDeal = createAsyncThunk(
  'crm/updateDeal',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/crm/deals/${id}`, data);
      return response.data.deal || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Failed to update deal');
    }
  }
);

export const moveDealStage = createAsyncThunk(
  'crm/moveDealStage',
  async ({ id, stage }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/crm/deals/${id}/stage`, { stage });
      return response.data.deal || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Failed to move deal');
    }
  }
);

export const markDealWon = createAsyncThunk(
  'crm/markDealWon',
  async ({ id, reason, winReason, note }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/crm/deals/${id}/won`, { reason, winReason, note });
      return { deal: response.data.deal || response.data, promotedToCustomer: response.data.promotedToCustomer };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Failed to mark deal won');
    }
  }
);

export const markDealLost = createAsyncThunk(
  'crm/markDealLost',
  async ({ id, reason, lostReason, note, competitorName }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/crm/deals/${id}/lost`, { reason, lostReason, note, competitorName });
      return response.data.deal || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Failed to mark deal lost');
    }
  }
);

// Deal aging alerts — deals whose current stage has gone stale beyond `days`.
export const fetchStaleDeals = createAsyncThunk(
  'crm/fetchStaleDeals',
  async (days = 14, { rejectWithValue }) => {
    try {
      const response = await api.get('/crm/deals/stale', { params: { days } });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Failed to fetch stale deals');
    }
  }
);

// Forecast rollup by rep (assignedTo) for the CRM dashboard.
export const fetchForecastByRep = createAsyncThunk(
  'crm/fetchForecastByRep',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/crm/deals/forecast-by-rep');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Failed to fetch forecast by rep');
    }
  }
);

export const deleteDeal = createAsyncThunk(
  'crm/deleteDeal',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/crm/deals/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Failed to delete deal');
    }
  }
);

export const fetchKanban = createAsyncThunk(
  'crm/fetchKanban',
  async (pipelineId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/crm/deals/kanban/${pipelineId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch kanban');
    }
  }
);

// ─── CRM Invoices ────────────────────────────────────────
export const fetchCrmInvoices = createAsyncThunk(
  'crm/fetchCrmInvoices',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/crm/invoices', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch invoices');
    }
  }
);

export const createCrmInvoice = createAsyncThunk(
  'crm/createCrmInvoice',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/crm/invoices', data);
      return response.data.invoice || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create invoice');
    }
  }
);

export const updateCrmInvoice = createAsyncThunk(
  'crm/updateCrmInvoice',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/crm/invoices/${id}`, data);
      return response.data.invoice || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update invoice');
    }
  }
);

export const deleteCrmInvoice = createAsyncThunk(
  'crm/deleteCrmInvoice',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/crm/invoices/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete invoice');
    }
  }
);

export const markCrmInvoicePaid = createAsyncThunk(
  'crm/markCrmInvoicePaid',
  async ({ id, paidDate }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/crm/invoices/${id}/paid`, { paidDate });
      return response.data.invoice || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to mark invoice paid');
    }
  }
);

export const markCrmInvoiceSent = createAsyncThunk(
  'crm/markCrmInvoiceSent',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.post(`/crm/invoices/${id}/send`);
      return response.data.invoice || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to mark invoice sent');
    }
  }
);

export const sendCrmInvoiceViaWhatsApp = createAsyncThunk(
  'crm/sendCrmInvoiceViaWhatsApp',
  async ({ id, templateName, recipient }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/crm/invoices/${id}/send-whatsapp`, { templateName, recipient });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to send invoice via WhatsApp');
    }
  }
);

export const sendCrmInvoiceViaEmail = createAsyncThunk(
  'crm/sendCrmInvoiceViaEmail',
  async ({ id, recipient }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/crm/invoices/${id}/send-email`, { recipient });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to send invoice via email');
    }
  }
);

export const sendCrmInvoiceViaInstagram = createAsyncThunk(
  'crm/sendCrmInvoiceViaInstagram',
  async ({ id, recipient }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/crm/invoices/${id}/send-instagram`, { recipient });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to send invoice via Instagram');
    }
  }
);

export const fetchCrmQuotations = createAsyncThunk(
  'crm/fetchCrmQuotations',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/crm/quotations', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch quotations');
    }
  }
);

export const createCrmQuotation = createAsyncThunk(
  'crm/createCrmQuotation',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/crm/quotations', data);
      return response.data.quotation || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create quotation');
    }
  }
);

export const updateCrmQuotation = createAsyncThunk(
  'crm/updateCrmQuotation',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/crm/quotations/${id}`, data);
      return response.data.quotation || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update quotation');
    }
  }
);

export const deleteCrmQuotation = createAsyncThunk(
  'crm/deleteCrmQuotation',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/crm/quotations/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete quotation');
    }
  }
);

export const markCrmQuotationSent = createAsyncThunk(
  'crm/markCrmQuotationSent',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.post(`/crm/quotations/${id}/send`);
      return response.data.quotation || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to mark quotation sent');
    }
  }
);

export const sendCrmQuotationViaWhatsApp = createAsyncThunk(
  'crm/sendCrmQuotationViaWhatsApp',
  async ({ id, recipient }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/crm/quotations/${id}/send-whatsapp`, { recipient });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to send quotation via WhatsApp');
    }
  }
);

export const sendCrmQuotationViaEmail = createAsyncThunk(
  'crm/sendCrmQuotationViaEmail',
  async ({ id, recipient }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/crm/quotations/${id}/send-email`, { recipient });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to send quotation via email');
    }
  }
);

export const markCrmQuotationAccepted = createAsyncThunk(
  'crm/markCrmQuotationAccepted',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.post(`/crm/quotations/${id}/accept`);
      return response.data.quotation || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to mark quotation accepted');
    }
  }
);

export const markCrmQuotationRejected = createAsyncThunk(
  'crm/markCrmQuotationRejected',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.post(`/crm/quotations/${id}/reject`);
      return response.data.quotation || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to mark quotation rejected');
    }
  }
);

export const convertCrmQuotationToInvoice = createAsyncThunk(
  'crm/convertCrmQuotationToInvoice',
  async ({ id, ...overrides }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/crm/quotations/${id}/convert-to-invoice`, overrides);
      return { quotationId: id, invoice: response.data.invoice, quotation: response.data.quotation };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to convert quotation to invoice');
    }
  }
);

export const createCrmQuotationFromDeal = createAsyncThunk(
  'crm/createCrmQuotationFromDeal',
  async ({ dealId, ...overrides }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/crm/quotations/from-deal/${dealId}`, overrides || {});
      return response.data.quotation || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create quotation from deal');
    }
  }
);

export const linkContactInstagram = createAsyncThunk(
  'crm/linkContactInstagram',
  async ({ id, conversationId }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/crm/contacts/${id}/link-instagram`, { conversationId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to link Instagram conversation');
    }
  }
);

export const linkContactFacebook = createAsyncThunk(
  'crm/linkContactFacebook',
  async ({ id, conversationId }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/crm/contacts/${id}/link-facebook`, { conversationId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to link Facebook conversation');
    }
  }
);

export const cancelCrmInvoice = createAsyncThunk(
  'crm/cancelCrmInvoice',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.post(`/crm/invoices/${id}/cancel`);
      return response.data.invoice || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to cancel invoice');
    }
  }
);

export const generateCrmInvoicePaymentLink = createAsyncThunk(
  'crm/generateCrmInvoicePaymentLink',
  async ({ id, gateway }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/crm/invoices/${id}/payment-link`, { gateway });
      return response.data.invoice || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to generate payment link');
    }
  }
);

export const markCrmInvoiceViewed = createAsyncThunk(
  'crm/markCrmInvoiceViewed',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.post(`/crm/invoices/${id}/viewed`);
      return response.data.invoice || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to mark invoice viewed');
    }
  }
);

export const markCrmInvoiceRefunded = createAsyncThunk(
  'crm/markCrmInvoiceRefunded',
  async ({ id, reason }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/crm/invoices/${id}/refund`, { reason });
      return response.data.invoice || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to mark invoice refunded');
    }
  }
);

export const voidCrmInvoice = createAsyncThunk(
  'crm/voidCrmInvoice',
  async ({ id, reason }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/crm/invoices/${id}/void`, { reason });
      return response.data.invoice || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to void invoice');
    }
  }
);

export const recordCrmInvoicePayment = createAsyncThunk(
  'crm/recordCrmInvoicePayment',
  async ({ id, amount, paidDate }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/crm/invoices/${id}/record-payment`, { amount, paidDate });
      return response.data.invoice || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to record payment');
    }
  }
);

export const toggleCrmInvoiceRecurringPause = createAsyncThunk(
  'crm/toggleCrmInvoiceRecurringPause',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.post(`/crm/invoices/${id}/toggle-recurring-pause`);
      return response.data.invoice || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to toggle recurring pause');
    }
  }
);

export const createCrmInvoiceFromDeal = createAsyncThunk(
  'crm/createCrmInvoiceFromDeal',
  async ({ dealId, overrides }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/crm/invoices/from-deal/${dealId}`, overrides || {});
      return response.data.invoice || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to generate invoice from deal');
    }
  }
);

// ─── Pipelines ───────────────────────────────────────────
export const fetchPipelines = createAsyncThunk(
  'crm/fetchPipelines',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/crm/pipelines');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch pipelines');
    }
  }
);

export const createPipeline = createAsyncThunk(
  'crm/createPipeline',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/crm/pipelines', data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create pipeline');
    }
  }
);

export const updatePipeline = createAsyncThunk(
  'crm/updatePipeline',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/crm/pipelines/${id}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update pipeline');
    }
  }
);

export const deletePipeline = createAsyncThunk(
  'crm/deletePipeline',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/crm/pipelines/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete pipeline');
    }
  }
);

// ─── Tasks ───────────────────────────────────────────────
export const fetchTasks = createAsyncThunk(
  'crm/fetchTasks',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/crm/tasks', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch tasks');
    }
  }
);

export const createTask = createAsyncThunk(
  'crm/createTask',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/crm/tasks', data);
      return response.data.task || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Failed to create task');
    }
  }
);

export const completeTask = createAsyncThunk(
  'crm/completeTask',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.post(`/crm/tasks/${id}/complete`);
      return response.data.task || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Failed to complete task');
    }
  }
);

export const updateTask = createAsyncThunk(
  'crm/updateTask',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/crm/tasks/${id}`, data);
      return response.data.task || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Failed to update task');
    }
  }
);

export const deleteTask = createAsyncThunk(
  'crm/deleteTask',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/crm/tasks/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete task');
    }
  }
);

// ─── Activity Templates ─────────────────────────────────
export const fetchActivityTemplates = createAsyncThunk(
  'crm/fetchActivityTemplates',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/crm/activity-templates', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch activity templates');
    }
  }
);

export const createActivityTemplate = createAsyncThunk(
  'crm/createActivityTemplate',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/crm/activity-templates', data);
      return response.data.template || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create activity template');
    }
  }
);

export const updateActivityTemplate = createAsyncThunk(
  'crm/updateActivityTemplate',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/crm/activity-templates/${id}`, data);
      return response.data.template || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update activity template');
    }
  }
);

export const deleteActivityTemplate = createAsyncThunk(
  'crm/deleteActivityTemplate',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/crm/activity-templates/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete activity template');
    }
  }
);

export const applyActivityTemplate = createAsyncThunk(
  'crm/applyActivityTemplate',
  async ({ id, leadId, dealId, contactId, startDate }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/crm/activity-templates/${id}/apply`, { leadId, dealId, contactId, startDate });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to apply activity template');
    }
  }
);

// ─── Assignment Rules ───────────────────────────────────
export const fetchAssignmentRules = createAsyncThunk(
  'crm/fetchAssignmentRules',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/crm/assignment-rules', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch assignment rules');
    }
  }
);

export const createAssignmentRule = createAsyncThunk(
  'crm/createAssignmentRule',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/crm/assignment-rules', data);
      return response.data.rule || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create assignment rule');
    }
  }
);

export const updateAssignmentRule = createAsyncThunk(
  'crm/updateAssignmentRule',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/crm/assignment-rules/${id}`, data);
      return response.data.rule || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update assignment rule');
    }
  }
);

export const deleteAssignmentRule = createAsyncThunk(
  'crm/deleteAssignmentRule',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/crm/assignment-rules/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete assignment rule');
    }
  }
);

// ─── Sharing Rules ───────────────────────────────────────
export const fetchTaxProfiles = createAsyncThunk(
  'crm/fetchTaxProfiles',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/crm/tax-profiles', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch tax profiles');
    }
  }
);

export const createTaxProfile = createAsyncThunk(
  'crm/createTaxProfile',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/crm/tax-profiles', data);
      return response.data.profile || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create tax profile');
    }
  }
);

export const updateTaxProfile = createAsyncThunk(
  'crm/updateTaxProfile',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/crm/tax-profiles/${id}`, data);
      return response.data.profile || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update tax profile');
    }
  }
);

export const deleteTaxProfile = createAsyncThunk(
  'crm/deleteTaxProfile',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/crm/tax-profiles/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete tax profile');
    }
  }
);

export const fetchSharingRules = createAsyncThunk(
  'crm/fetchSharingRules',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/crm/sharing-rules', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch sharing rules');
    }
  }
);

export const createSharingRule = createAsyncThunk(
  'crm/createSharingRule',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/crm/sharing-rules', data);
      return response.data.rule || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create sharing rule');
    }
  }
);

export const updateSharingRule = createAsyncThunk(
  'crm/updateSharingRule',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/crm/sharing-rules/${id}`, data);
      return response.data.rule || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update sharing rule');
    }
  }
);

export const deleteSharingRule = createAsyncThunk(
  'crm/deleteSharingRule',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/crm/sharing-rules/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete sharing rule');
    }
  }
);

// ─── Saved List Views ───────────────────────────────────
export const fetchSavedListViews = createAsyncThunk(
  'crm/fetchSavedListViews',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/crm/list-views', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch saved views');
    }
  }
);

export const createSavedListView = createAsyncThunk(
  'crm/createSavedListView',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/crm/list-views', data);
      return response.data.view || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to save view');
    }
  }
);

export const updateSavedListView = createAsyncThunk(
  'crm/updateSavedListView',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/crm/list-views/${id}`, data);
      return response.data.view || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update saved view');
    }
  }
);

export const deleteSavedListView = createAsyncThunk(
  'crm/deleteSavedListView',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/crm/list-views/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete saved view');
    }
  }
);

// ─── Lead Scraper ────────────────────────────────────────
export const runLeadScraper = createAsyncThunk(
  'crm/runLeadScraper',
  async ({ location, businessType, limit }, { rejectWithValue }) => {
    try {
      const response = await api.post('/crm/lead-scraper/run', { location, businessType, limit });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to run lead scraper');
    }
  }
);

// ─── Activities ──────────────────────────────────────────
export const fetchActivities = createAsyncThunk(
  'crm/fetchActivities',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/crm/activities', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch activities');
    }
  }
);

export const createActivity = createAsyncThunk(
  'crm/createActivity',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/crm/activities', data);
      return response.data.activity || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Failed to log activity');
    }
  }
);

// ─── Analytics ───────────────────────────────────────────
export const fetchCrmDashboard = createAsyncThunk(
  'crm/fetchCrmDashboard',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/crm/analytics/dashboard');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch dashboard');
    }
  }
);

export const fetchFunnel = createAsyncThunk(
  'crm/fetchFunnel',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/crm/analytics/funnel');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch funnel');
    }
  }
);

export const fetchRevenueForecast = createAsyncThunk(
  'crm/fetchRevenueForecast',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/crm/analytics/revenue-forecast');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch revenue forecast');
    }
  }
);

// ─── Contacts ───────────────────────────────────────────────
export const fetchContacts = createAsyncThunk(
  'crm/fetchContacts',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/crm/contacts', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch contacts');
    }
  }
);

export const createContact = createAsyncThunk(
  'crm/createContact',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/crm/contacts', data);
      return response.data.contact || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create contact');
    }
  }
);

export const updateContact = createAsyncThunk(
  'crm/updateContact',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/crm/contacts/${id}`, data);
      return response.data.contact || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update contact');
    }
  }
);

export const deleteContact = createAsyncThunk(
  'crm/deleteContact',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/crm/contacts/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete contact');
    }
  }
);

// ─── Accounts ────────────────────────────────────────────────
export const fetchAccounts = createAsyncThunk(
  'crm/fetchAccounts',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/crm/accounts', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch accounts');
    }
  }
);

export const createAccount = createAsyncThunk(
  'crm/createAccount',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/crm/accounts', data);
      return response.data.account || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create account');
    }
  }
);

export const updateAccount = createAsyncThunk(
  'crm/updateAccount',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/crm/accounts/${id}`, data);
      return response.data.account || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update account');
    }
  }
);

export const deleteAccount = createAsyncThunk(
  'crm/deleteAccount',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/crm/accounts/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete account');
    }
  }
);

// ─── Initial State ────────────────────────────────────────
const initialState = {
  leads: [],
  leadsMeta: { total: 0, page: 1, pages: 1 },
  deals: [],
  staleDeals: [],
  staleDealsThreshold: 14,
  contacts: [],
  contactsMeta: { total: 0, page: 1, pages: 1 },
  accounts: [],
  pipelines: [],
  kanban: {},
  tasks: [],
  activityTemplates: [],
  assignmentRules: [],
  sharingRules: [],
  taxProfiles: [],
  savedListViews: [],
  invoices: [],
  invoicesMeta: { total: 0, page: 1, pages: 1 },
  quotations: [],
  quotationsMeta: { total: 0, page: 1, pages: 1 },
  activities: [],
  analytics: {
    dashboard: null,
    funnel: [],
    forecast: [],
    forecastByRep: [],
  },
  loading: false,
  error: null,
};

// ─── Slice ────────────────────────────────────────────────
const crmSlice = createSlice({
  name: 'crm',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    moveDealLocal: (state, action) => {
      // Optimistic update for kanban drag
      const { dealId, newStage } = action.payload;
      const deal = state.deals.find(d => d._id === dealId);
      if (deal) deal.stage = newStage;
    },
  },
  extraReducers: (builder) => {
    // ── Leads ──
    builder
      .addCase(fetchLeads.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchLeads.fulfilled, (state, action) => {
        state.loading = false;
        state.leads = Array.isArray(action.payload) ? action.payload : (action.payload.leads || []);
        if (!Array.isArray(action.payload)) {
          state.leadsMeta = {
            total: action.payload.total ?? state.leads.length,
            page: action.payload.page ?? 1,
            pages: action.payload.pages ?? 1,
          };
        }
      })
      .addCase(fetchLeads.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(createLead.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(createLead.fulfilled, (state, action) => {
        state.loading = false;
        state.leads.unshift(action.payload);
      })
      .addCase(createLead.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(updateLead.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(updateLead.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.leads.findIndex(l => l._id === action.payload._id);
        if (idx !== -1) state.leads[idx] = action.payload;
      })
      .addCase(updateLead.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(deleteLead.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(deleteLead.fulfilled, (state, action) => {
        state.loading = false;
        state.leads = state.leads.filter(l => l._id !== action.payload);
      })
      .addCase(deleteLead.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(convertLead.fulfilled, (state, action) => {
        const idx = state.leads.findIndex(l => l._id === action.payload._id);
        if (idx !== -1) state.leads[idx] = action.payload;
      })

    // ── Deals ──
      .addCase(fetchDeals.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchDeals.fulfilled, (state, action) => {
        state.loading = false;
        state.deals = Array.isArray(action.payload) ? action.payload : (action.payload.deals || []);
      })
      .addCase(fetchDeals.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(createDeal.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(createDeal.fulfilled, (state, action) => {
        state.loading = false;
        state.deals.unshift(action.payload);
      })
      .addCase(createDeal.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(updateDeal.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(updateDeal.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.deals.findIndex(d => d._id === action.payload._id);
        if (idx !== -1) state.deals[idx] = action.payload;
      })
      .addCase(updateDeal.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(moveDealStage.pending, (state, action) => {
        // Optimistic move — without this the dragged card visually snaps back
        // to its original column the instant it's dropped and only reappears
        // in the new column once the PUT round-trips, which reads as "broken"
        // drag-and-drop on any real network latency. Stash the prior stage so
        // a failed request can be rolled back instead of leaving the board
        // showing a stage the server never actually saved.
        const { id, stage } = action.meta.arg;
        const idx = state.deals.findIndex(d => d._id === id);
        if (idx !== -1) {
          state.dealStageRollback = state.dealStageRollback || {};
          state.dealStageRollback[id] = state.deals[idx].stage;
          state.deals[idx].stage = stage;
        }
      })
      .addCase(moveDealStage.fulfilled, (state, action) => {
        const idx = state.deals.findIndex(d => d._id === action.payload._id);
        if (idx !== -1) state.deals[idx] = action.payload;
        if (state.dealStageRollback) delete state.dealStageRollback[action.payload._id];
      })
      .addCase(moveDealStage.rejected, (state, action) => {
        state.error = action.payload;
        const { id } = action.meta.arg;
        const prevStage = state.dealStageRollback?.[id];
        if (prevStage !== undefined) {
          const idx = state.deals.findIndex(d => d._id === id);
          if (idx !== -1) state.deals[idx].stage = prevStage;
          delete state.dealStageRollback[id];
        }
      })

      .addCase(markDealWon.pending, (state) => { state.loading = true; })
      .addCase(markDealWon.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.deals.findIndex(d => d._id === action.payload.deal._id);
        if (idx !== -1) state.deals[idx] = action.payload.deal;
      })
      .addCase(markDealWon.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(markDealLost.pending, (state) => { state.loading = true; })
      .addCase(markDealLost.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.deals.findIndex(d => d._id === action.payload._id);
        if (idx !== -1) state.deals[idx] = action.payload;
      })
      .addCase(markDealLost.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(fetchStaleDeals.fulfilled, (state, action) => {
        state.staleDeals = action.payload?.deals || [];
        state.staleDealsThreshold = action.payload?.daysThreshold || state.staleDealsThreshold;
      })
      .addCase(fetchStaleDeals.rejected, (state, action) => { state.error = action.payload; })

      .addCase(deleteDeal.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(deleteDeal.fulfilled, (state, action) => {
        state.loading = false;
        state.deals = state.deals.filter(d => d._id !== action.payload);
      })
      .addCase(deleteDeal.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(fetchKanban.pending, (state) => { state.loading = true; })
      .addCase(fetchKanban.fulfilled, (state, action) => {
        state.loading = false;
        state.kanban = action.payload.kanban || action.payload;
      })
      .addCase(fetchKanban.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

    // ── CRM Invoices ──
      .addCase(fetchCrmInvoices.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchCrmInvoices.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices = action.payload.invoices || [];
        state.invoicesMeta = {
          total: action.payload.total ?? 0,
          page: action.payload.page ?? 1,
          pages: action.payload.pages ?? 1,
        };
      })
      .addCase(fetchCrmInvoices.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(createCrmInvoice.fulfilled, (state, action) => {
        state.invoices.unshift(action.payload);
      })

      .addCase(updateCrmInvoice.fulfilled, (state, action) => {
        const idx = state.invoices.findIndex(i => i._id === action.payload._id);
        if (idx !== -1) state.invoices[idx] = action.payload;
      })

      .addCase(deleteCrmInvoice.fulfilled, (state, action) => {
        state.invoices = state.invoices.filter(i => i._id !== action.payload);
      })

      .addCase(markCrmInvoicePaid.fulfilled, (state, action) => {
        const idx = state.invoices.findIndex(i => i._id === action.payload._id);
        if (idx !== -1) state.invoices[idx] = action.payload;
      })

      .addCase(markCrmInvoiceViewed.fulfilled, (state, action) => {
        const idx = state.invoices.findIndex(i => i._id === action.payload._id);
        if (idx !== -1) state.invoices[idx] = action.payload;
      })

      .addCase(generateCrmInvoicePaymentLink.fulfilled, (state, action) => {
        const idx = state.invoices.findIndex(i => i._id === action.payload._id);
        if (idx !== -1) state.invoices[idx] = action.payload;
      })

      .addCase(markCrmInvoiceRefunded.fulfilled, (state, action) => {
        const idx = state.invoices.findIndex(i => i._id === action.payload._id);
        if (idx !== -1) state.invoices[idx] = action.payload;
      })

      .addCase(voidCrmInvoice.fulfilled, (state, action) => {
        const idx = state.invoices.findIndex(i => i._id === action.payload._id);
        if (idx !== -1) state.invoices[idx] = action.payload;
      })

      .addCase(markCrmInvoiceSent.fulfilled, (state, action) => {
        const idx = state.invoices.findIndex(i => i._id === action.payload._id);
        if (idx !== -1) state.invoices[idx] = action.payload;
      })

      .addCase(cancelCrmInvoice.fulfilled, (state, action) => {
        const idx = state.invoices.findIndex(i => i._id === action.payload._id);
        if (idx !== -1) state.invoices[idx] = action.payload;
      })

      .addCase(recordCrmInvoicePayment.fulfilled, (state, action) => {
        const idx = state.invoices.findIndex(i => i._id === action.payload._id);
        if (idx !== -1) state.invoices[idx] = action.payload;
      })

      .addCase(toggleCrmInvoiceRecurringPause.fulfilled, (state, action) => {
        const idx = state.invoices.findIndex(i => i._id === action.payload._id);
        if (idx !== -1) state.invoices[idx] = action.payload;
      })

      .addCase(createCrmInvoiceFromDeal.fulfilled, (state, action) => {
        state.invoices.unshift(action.payload);
      })

    // ── CRM Quotations ──
      .addCase(fetchCrmQuotations.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchCrmQuotations.fulfilled, (state, action) => {
        state.loading = false;
        state.quotations = action.payload.quotations || [];
        state.quotationsMeta = {
          total: action.payload.total ?? 0,
          page: action.payload.page ?? 1,
          pages: action.payload.pages ?? 1,
        };
      })
      .addCase(fetchCrmQuotations.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(createCrmQuotation.fulfilled, (state, action) => {
        state.quotations.unshift(action.payload);
      })

      .addCase(updateCrmQuotation.fulfilled, (state, action) => {
        const idx = state.quotations.findIndex(q => q._id === action.payload._id);
        if (idx !== -1) state.quotations[idx] = action.payload;
      })

      .addCase(deleteCrmQuotation.fulfilled, (state, action) => {
        state.quotations = state.quotations.filter(q => q._id !== action.payload);
      })

      .addCase(markCrmQuotationSent.fulfilled, (state, action) => {
        const idx = state.quotations.findIndex(q => q._id === action.payload._id);
        if (idx !== -1) state.quotations[idx] = action.payload;
      })

      .addCase(markCrmQuotationAccepted.fulfilled, (state, action) => {
        const idx = state.quotations.findIndex(q => q._id === action.payload._id);
        if (idx !== -1) state.quotations[idx] = action.payload;
      })

      .addCase(markCrmQuotationRejected.fulfilled, (state, action) => {
        const idx = state.quotations.findIndex(q => q._id === action.payload._id);
        if (idx !== -1) state.quotations[idx] = action.payload;
      })

      .addCase(convertCrmQuotationToInvoice.fulfilled, (state, action) => {
        const idx = state.quotations.findIndex(q => q._id === action.payload.quotationId);
        if (idx !== -1) {
          const q = action.payload.quotation;
          state.quotations[idx].status = q?.status || 'converted';
          state.quotations[idx].invoicedAmount = q?.invoicedAmount;
          state.quotations[idx].convertedToInvoiceId = q?.convertedToInvoiceId || action.payload.invoice?._id;
        }
        if (action.payload.invoice) state.invoices.unshift(action.payload.invoice);
      })

      .addCase(createCrmQuotationFromDeal.fulfilled, (state, action) => {
        state.quotations.unshift(action.payload);
      })

    // ── Pipelines ──
      .addCase(fetchPipelines.pending, (state) => { state.loading = true; })
      .addCase(fetchPipelines.fulfilled, (state, action) => {
        state.loading = false;
        state.pipelines = action.payload.pipelines || action.payload;
      })
      .addCase(fetchPipelines.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(createPipeline.fulfilled, (state, action) => {
        const pipeline = action.payload.pipeline || action.payload;
        state.pipelines.push(pipeline);
      })
      .addCase(updatePipeline.fulfilled, (state, action) => {
        const pipeline = action.payload.pipeline || action.payload;
        const idx = state.pipelines.findIndex(p => p._id === pipeline._id);
        if (idx !== -1) state.pipelines[idx] = pipeline;
      })
      .addCase(deletePipeline.fulfilled, (state, action) => {
        state.pipelines = state.pipelines.filter(p => p._id !== action.payload);
      })

    // ── Tasks ──
      .addCase(fetchTasks.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = action.payload.tasks || action.payload;
      })
      .addCase(fetchTasks.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(createTask.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(createTask.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks.unshift(action.payload);
      })
      .addCase(createTask.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(completeTask.pending, (state) => { state.loading = true; })
      .addCase(completeTask.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.tasks.findIndex(t => t._id === action.payload._id);
        if (idx !== -1) state.tasks[idx] = action.payload;
      })
      .addCase(completeTask.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(updateTask.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(updateTask.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.tasks.findIndex(t => t._id === action.payload._id);
        if (idx !== -1) state.tasks[idx] = action.payload;
      })
      .addCase(updateTask.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(deleteTask.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(deleteTask.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = state.tasks.filter(t => t._id !== action.payload);
      })
      .addCase(deleteTask.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

    // ── Activity Templates ──
      .addCase(fetchActivityTemplates.fulfilled, (state, action) => {
        state.activityTemplates = action.payload.templates || action.payload;
      })
      .addCase(createActivityTemplate.fulfilled, (state, action) => {
        state.activityTemplates.unshift(action.payload);
      })
      .addCase(updateActivityTemplate.fulfilled, (state, action) => {
        const idx = state.activityTemplates.findIndex(t => t._id === action.payload._id);
        if (idx !== -1) state.activityTemplates[idx] = action.payload;
      })
      .addCase(deleteActivityTemplate.fulfilled, (state, action) => {
        state.activityTemplates = state.activityTemplates.filter(t => t._id !== action.payload);
      })
      .addCase(applyActivityTemplate.fulfilled, (state, action) => {
        if (Array.isArray(action.payload.tasks)) {
          state.tasks.unshift(...action.payload.tasks);
        }
      })

    // ── Assignment Rules ──
      .addCase(fetchAssignmentRules.fulfilled, (state, action) => {
        state.assignmentRules = action.payload.rules || action.payload;
      })
      .addCase(createAssignmentRule.fulfilled, (state, action) => {
        state.assignmentRules.unshift(action.payload);
      })
      .addCase(updateAssignmentRule.fulfilled, (state, action) => {
        const idx = state.assignmentRules.findIndex(r => r._id === action.payload._id);
        if (idx !== -1) state.assignmentRules[idx] = action.payload;
      })
      .addCase(deleteAssignmentRule.fulfilled, (state, action) => {
        state.assignmentRules = state.assignmentRules.filter(r => r._id !== action.payload);
      })

    // ── Sharing Rules ──
      .addCase(fetchTaxProfiles.fulfilled, (state, action) => {
        state.taxProfiles = action.payload.profiles || action.payload;
      })
      .addCase(createTaxProfile.fulfilled, (state, action) => {
        state.taxProfiles.unshift(action.payload);
      })
      .addCase(updateTaxProfile.fulfilled, (state, action) => {
        const idx = state.taxProfiles.findIndex(p => p._id === action.payload._id);
        if (idx !== -1) state.taxProfiles[idx] = action.payload;
      })
      .addCase(deleteTaxProfile.fulfilled, (state, action) => {
        state.taxProfiles = state.taxProfiles.filter(p => p._id !== action.payload);
      })
      .addCase(fetchSharingRules.fulfilled, (state, action) => {
        state.sharingRules = action.payload.rules || action.payload;
      })
      .addCase(createSharingRule.fulfilled, (state, action) => {
        state.sharingRules.unshift(action.payload);
      })
      .addCase(updateSharingRule.fulfilled, (state, action) => {
        const idx = state.sharingRules.findIndex(r => r._id === action.payload._id);
        if (idx !== -1) state.sharingRules[idx] = action.payload;
      })
      .addCase(deleteSharingRule.fulfilled, (state, action) => {
        state.sharingRules = state.sharingRules.filter(r => r._id !== action.payload);
      })

    // ── Saved List Views ──
      .addCase(fetchSavedListViews.fulfilled, (state, action) => {
        state.savedListViews = action.payload.views || action.payload;
      })
      .addCase(createSavedListView.fulfilled, (state, action) => {
        state.savedListViews.push(action.payload);
      })
      .addCase(updateSavedListView.fulfilled, (state, action) => {
        const idx = state.savedListViews.findIndex(v => v._id === action.payload._id);
        if (idx !== -1) state.savedListViews[idx] = action.payload;
      })
      .addCase(deleteSavedListView.fulfilled, (state, action) => {
        state.savedListViews = state.savedListViews.filter(v => v._id !== action.payload);
      })

    // ── Activities ──
      .addCase(fetchActivities.pending, (state) => { state.loading = true; })
      .addCase(fetchActivities.fulfilled, (state, action) => {
        state.loading = false;
        state.activities = action.payload.activities || action.payload;
      })
      .addCase(fetchActivities.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(createActivity.fulfilled, (state, action) => {
        state.activities.unshift(action.payload);
      })

    // ── Contacts ──
      .addCase(fetchContacts.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchContacts.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(action.payload)) {
          state.contacts = action.payload;
          state.contactsMeta = { total: action.payload.length, page: 1, pages: 1 };
        } else {
          state.contacts = action.payload.contacts || [];
          state.contactsMeta = {
            total: action.payload.total ?? action.payload.contacts?.length ?? 0,
            page: action.payload.page ?? 1,
            pages: action.payload.pages ?? 1,
          };
        }
      })
      .addCase(fetchContacts.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(createContact.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(createContact.fulfilled, (state, action) => {
        state.loading = false;
        state.contacts.unshift(action.payload);
      })
      .addCase(createContact.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(updateContact.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(updateContact.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.contacts.findIndex(c => c._id === action.payload._id);
        if (idx !== -1) state.contacts[idx] = action.payload;
      })
      .addCase(updateContact.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(deleteContact.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(deleteContact.fulfilled, (state, action) => {
        state.loading = false;
        state.contacts = state.contacts.filter(c => c._id !== action.payload);
      })
      .addCase(deleteContact.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

    // ── Accounts ──
      .addCase(fetchAccounts.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchAccounts.fulfilled, (state, action) => {
        state.loading = false;
        state.accounts = Array.isArray(action.payload) ? action.payload : (action.payload.accounts || []);
      })
      .addCase(fetchAccounts.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(createAccount.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(createAccount.fulfilled, (state, action) => {
        state.loading = false;
        state.accounts.unshift(action.payload);
      })
      .addCase(createAccount.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(updateAccount.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(updateAccount.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.accounts.findIndex(a => a._id === action.payload._id);
        if (idx !== -1) state.accounts[idx] = action.payload;
      })
      .addCase(updateAccount.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(deleteAccount.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(deleteAccount.fulfilled, (state, action) => {
        state.loading = false;
        state.accounts = state.accounts.filter(a => a._id !== action.payload);
      })
      .addCase(deleteAccount.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

    // ── Analytics ──
      .addCase(fetchCrmDashboard.pending, (state) => { state.loading = true; })
      .addCase(fetchCrmDashboard.fulfilled, (state, action) => {
        state.loading = false;
        state.analytics.dashboard = action.payload.dashboard || action.payload;
      })
      .addCase(fetchCrmDashboard.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(fetchFunnel.fulfilled, (state, action) => {
        state.analytics.funnel = action.payload.funnel || action.payload;
      })

      .addCase(fetchRevenueForecast.fulfilled, (state, action) => {
        state.analytics.forecast = action.payload.forecast || action.payload;
      })

      .addCase(fetchForecastByRep.fulfilled, (state, action) => {
        state.analytics.forecastByRep = action.payload.forecast || action.payload;
      });
  },
});

export const { clearError, moveDealLocal } = crmSlice.actions;
export default crmSlice.reducer;
